/**
 * Epic Bubble Chart Component
 * 
 * Interactive bubble chart visualizing epics by completion date with sophisticated
 * bubble sizing, collision detection, and dual-color coding system.
 * 
 * Features:
 * - Bubble sizing proportional to story points
 * - Dual-color system: inner bubble (story points), border (bug count)
 * - Intelligent collision detection and positioning
 * - Click navigation to epic details
 * - Hover interactions with detailed tooltips
 * - Performance optimizations for large datasets
 * - Responsive design with mobile touch support
 * 
 * Color Coding:
 * - Inner bubble: Blue intensity based on story points
 * - Border: Red thickness based on bug count
 * - Hover effects: Enhanced colors for better visibility
 * 
 * Positioning Algorithm:
 * - Chronological processing to maintain timeline accuracy
 * - Collision detection with minimum distance calculations
 * - Systematic layering with fallback to random positioning
 * - Y-axis positioning for visual separation
 * 
 * @param data - Array of bubble data points with epic and positioning information
 * @param loading - Whether the chart is in loading state
 * @param onBubbleHover - Callback fired when user hovers over a bubble
 * @param onBubbleClick - Callback fired when user clicks on a bubble
 * 
 * @example
 * ```tsx
 * <EpicBubbleChart
 *   data={bubbleData}
 *   loading={false}
 *   onBubbleHover={(epic) => setHoveredEpic(epic.epicKey)}
 *   onBubbleClick={(epic) => navigateToEpic(epic.epicKey)}
 * />
 * ```
 */

import React, { useMemo, memo, useCallback } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
  TooltipItem,
  TimeScale,
  BubbleController
} from 'chart.js'
import { Bubble } from 'react-chartjs-2'
import 'chartjs-adapter-luxon'
import { EpicBubbleChartProps, EpicBubbleDataPoint } from '../../../../types/epic-analytics-types'
import { BUBBLE_CHART_COLORS, CHART_COLORS } from '../../../../utils/epic-analytics-constants'
import { getEpicDetailsUrl } from '../../../../utils/epic-analytics-utils'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  BubbleController
)

/**
 * Calculate bubble radius based on story points
 */
const calculateBubbleRadius = (storyPoints: number, maxPoints: number): number => {
  const minRadius = 8
  const maxRadius = 40

  if (maxPoints === 0) return minRadius

  // Scale radius proportionally to story points
  const ratio = storyPoints / maxPoints
  return minRadius + (maxRadius - minRadius) * ratio
}

/**
 * Generate Y position with advanced collision detection
 */
const generateYPositions = (data: EpicBubbleDataPoint[]): EpicBubbleDataPoint[] => {
  if (data.length === 0) return data

  // Sort by completion date to process chronologically
  const sortedData = [...data].sort((a, b) =>
    new Date(a.completionDate).getTime() - new Date(b.completionDate).getTime()
  )

  const result: EpicBubbleDataPoint[] = []
  const occupiedPositions: Array<{ x: number; y: number; radius: number }> = []
  const maxStoryPoints = Math.max(...data.map(d => d.storyPoints))

  sortedData.forEach((epic, index) => {
    const bubbleRadius = calculateBubbleRadius(epic.storyPoints, maxStoryPoints)
    let yPosition = 50 // Start in middle
    let attempts = 0
    const maxAttempts = 30

    // Calculate minimum distance based on bubble sizes
    const getMinDistance = (pos: { radius: number }) => {
      return (bubbleRadius + pos.radius) * 1.2 // 20% padding
    }

    // Try systematic positioning first (layers)
    const layers = [50, 30, 70, 20, 80, 15, 85] // Preferred Y positions
    let layerIndex = 0

    while (attempts < maxAttempts) {
      // Use systematic layers first, then random
      if (layerIndex < layers.length) {
        yPosition = layers[layerIndex]
        layerIndex++
      } else {
        yPosition = 15 + Math.random() * 70 // Random position between 15-85
      }

      const hasCollision = occupiedPositions.some(pos => {
        const xDistance = Math.abs(epic.x - pos.x)
        const yDistance = Math.abs(yPosition - pos.y)
        const requiredDistance = getMinDistance(pos)

        return Math.sqrt(xDistance * xDistance + yDistance * yDistance) < requiredDistance
      })

      if (!hasCollision) {
        break
      }

      attempts++
    }

    // If still colliding after all attempts, use a fallback position
    if (attempts >= maxAttempts) {
      yPosition = 50 + (index % 3 - 1) * 25 // Spread vertically based on index
    }

    const updatedEpic = { ...epic, y: yPosition }
    result.push(updatedEpic)

    // Record this position as occupied
    occupiedPositions.push({
      x: epic.x,
      y: yPosition,
      radius: bubbleRadius
    })
  })

  return result
}

/**
 * Epic Bubble Chart - Epics visualization by completion date with story points sizing
 * Memoized for performance optimization
 */
const EpicBubbleChart: React.FC<EpicBubbleChartProps> = memo(({
  data,
  loading = false,
  onBubbleClick,
  onBubbleHover
}) => {
  // Process and optimize data for bubble chart
  const processedData = useMemo(() => {
    if (!data || data.length === 0) {
      return []
    }

    // Convert completion dates to timestamps for X positioning
    const dataWithTimestamps = data.map(epic => ({
      ...epic,
      x: new Date(epic.completionDate).getTime()
    }))

    // Generate Y positions with collision detection
    return generateYPositions(dataWithTimestamps)
  }, [data])

  // Calculate max story points for radius scaling
  const maxStoryPoints = useMemo(() => {
    if (processedData.length === 0) return 0
    return Math.max(...processedData.map(epic => epic.storyPoints))
  }, [processedData])

  // Prepare chart data
  const chartData = useMemo<ChartData<'bubble'>>(() => {
    if (processedData.length === 0) {
      return {
        datasets: []
      }
    }

    return {
      datasets: [
        {
          label: 'Epics',
          data: processedData.map(epic => ({
            x: epic.x,
            y: epic.y,
            r: calculateBubbleRadius(epic.storyPoints, maxStoryPoints)
          })),
          backgroundColor: processedData.map(epic => {
            // Inner color based on story points (blue gradient)
            const intensity = epic.storyPoints / maxStoryPoints
            const alpha = 0.4 + (intensity * 0.4) // 0.4 to 0.8 opacity
            return `rgba(59, 130, 246, ${alpha})`
          }),
          borderColor: processedData.map(epic => {
            // Border color based on bug count (red intensity)
            if (epic.bugCount === 0) {
              return CHART_COLORS.primary // Blue border for no bugs
            }

            // Calculate bug intensity (assuming max 20 bugs for scaling)
            const maxBugs = Math.max(...processedData.map(e => e.bugCount), 20)
            const bugIntensity = Math.min(epic.bugCount / maxBugs, 1)
            const alpha = 0.6 + (bugIntensity * 0.4) // 0.6 to 1.0 opacity
            return `rgba(239, 68, 68, ${alpha})` // Red border for bugs
          }),
          borderWidth: processedData.map(epic => {
            // Thicker border for more bugs
            return epic.bugCount > 0 ? 3 + Math.min(epic.bugCount, 5) : 2
          }),
          pointStyle: 'circle',
          // Hover effects
          hoverBackgroundColor: processedData.map(epic => {
            // Brighter on hover
            const intensity = epic.storyPoints / maxStoryPoints
            const alpha = 0.6 + (intensity * 0.3) // Increased opacity on hover
            return `rgba(59, 130, 246, ${alpha})`
          }),
          hoverBorderColor: processedData.map(epic => {
            return epic.bugCount > 0 ? 'rgba(239, 68, 68, 1)' : 'rgba(59, 130, 246, 1)'
          }),
          hoverBorderWidth: processedData.map(epic => {
            // Even thicker border on hover
            return epic.bugCount > 0 ? 4 + Math.min(epic.bugCount, 5) : 3
          }),
          // Store epic data for tooltips and interactions
          epicData: processedData
        }
      ]
    }
  }, [processedData, maxStoryPoints])

  // Chart options with enhanced interactions
  const chartOptions = useMemo<ChartOptions<'bubble'>>(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: processedData.length > 20 ? 500 : 1000
    },
    interaction: {
      intersect: false,
      mode: 'point'
    },
    plugins: {
      legend: {
        display: false // Hide legend since we have custom styling
      },
      title: {
        display: false
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: CHART_COLORS.primary,
        borderWidth: 2,
        cornerRadius: 12,
        displayColors: false,
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 12
        },
        position: 'nearest',
        caretPadding: 8,
        callbacks: {
          title: (tooltipItems: TooltipItem<'bubble'>[]) => {
            const item = tooltipItems[0]
            const epic = processedData[item.dataIndex]
            return epic.epicKey
          },
          beforeBody: (tooltipItems: TooltipItem<'bubble'>[]) => {
            const item = tooltipItems[0]
            const epic = processedData[item.dataIndex]
            // Truncate long titles
            const maxLength = 50
            const title = epic.epicTitle
            return title.length > maxLength ? `${title.substring(0, maxLength)}...` : title
          },
          label: (tooltipItem: TooltipItem<'bubble'>) => {
            const epic = processedData[tooltipItem.dataIndex]
            return [
              `Completion: ${new Date(epic.completionDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}`,
              `Story Points: ${epic.storyPoints}`,
              `Bugs: ${epic.bugCount}`
            ]
          },
          afterBody: () => {
            return ['', 'Click to view epic details']
          }
        }
      }
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: processedData.length > 50 ? 'week' : 'day',
          displayFormats: {
            day: 'MMM dd',
            week: 'MMM dd',
            month: 'MMM yyyy'
          },
          tooltipFormat: 'MMM dd, yyyy'
        },
        title: {
          display: true,
          text: 'Epic Completion Date',
          font: {
            size: 12,
            weight: 'bold'
          },
          color: 'rgb(75, 85, 99)' // gray-600
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
          lineWidth: 1
        },
        ticks: {
          maxTicksLimit: processedData.length > 30 ? 8 : 12,
          color: 'rgb(107, 114, 128)' // gray-500
        }
      },
      y: {
        display: false, // Hide Y axis as it's just for positioning
        min: 0,
        max: 100
      }
    },
    // Enhanced hover interactions
    onHover: (event, elements, chart) => {
      // Change cursor on hover
      if (chart.canvas) {
        chart.canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default'
      }

      // Call custom hover handler
      if (elements.length > 0 && onBubbleHover) {
        const elementIndex = elements[0].index
        const epic = processedData[elementIndex]
        onBubbleHover(epic)
      }
    },
    // Enhanced click handler for navigation
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const elementIndex = elements[0].index
        const epic = processedData[elementIndex]

        // Call custom click handler if provided
        if (onBubbleClick) {
          onBubbleClick(epic)
        } else {
          // Default behavior: open epic details in new tab
          const url = getEpicDetailsUrl(epic.epicKey)
          window.open(url, '_blank', 'noopener,noreferrer')
        }
      }
    }
  }), [processedData, onBubbleClick, onBubbleHover])

  if (loading) {
    return (
      <div className="epic-bubble-chart">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="epic-bubble-chart">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Epic Completion Overview
          </h3>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded">
            <div className="text-center">
              <p className="text-gray-500 mb-2">No epic data available</p>
              <p className="text-sm text-gray-400">
                Select a project and time period to view the bubble chart
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="epic-bubble-chart">
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Epic Completion Overview
          </h3>
          <div className="flex items-center space-x-4 text-xs text-gray-500">
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 rounded-full bg-blue-500 opacity-60"></div>
              <span>Story Points</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 rounded-full border-2 border-red-500"></div>
              <span>Bug Count</span>
            </div>
          </div>
        </div>
        <div className="h-64 relative">
          <Bubble data={chartData} options={chartOptions} />
        </div>
        <div className="mt-2 text-xs text-gray-500 text-center">
          Bubble size represents story points • Border thickness indicates bug count • Click bubbles for epic details
        </div>
      </div>
    </div>
  )
}, (prevProps, nextProps) => {
  // Custom comparison function for memo optimization
  return (
    prevProps.loading === nextProps.loading &&
    prevProps.data === nextProps.data &&
    prevProps.onBubbleHover === nextProps.onBubbleHover &&
    prevProps.onBubbleClick === nextProps.onBubbleClick
  )
})

EpicBubbleChart.displayName = 'EpicBubbleChart'

export { EpicBubbleChart }
export default EpicBubbleChart